import { createContext, useContext } from "solid-js";
import { createStore, reconcile } from "solid-js/store";

import {
  GetRepeatConfigsByAtype,
  GetRepeatDetailsByAtype,
  GetUserRepeatRankDetails,
} from "@/api/clientApi/activity";
import { RepeatActivity } from "@/enum/activity";
import { getUserChannel, userStore } from "@/stores/userStore";
import { DenyPackType } from "@/types/compontent/base/baseEnum";
import {
  RepeatActivityDetailItem,
  UserRepeatRankDetails,
} from "@/types/swagger/activity";
import { ActivityCfg } from "@/types/swagger/appserver";

// ========================
// Store 类型定义
// ========================
export interface CompetitionHistory {
  list: [];
  start: string;
  end: string;
  page: number;
  page_size: number;
  finish: boolean;
  loading: boolean;
}

export interface RepeatCompetitionStore {
  config: { [activity_id: string]: ActivityCfg };
  rank: UserRepeatRankDetails[];
  details: { [activity_id: string]: RepeatActivityDetailItem };
  history: CompetitionHistory;
}

// ========================
// 初始 Store 数据结构 —— 和界面使用严格匹配
// ========================
const initialCompetitionStore: RepeatCompetitionStore = {
  config: {},
  rank: [],
  details: {},
  history: {
    list: [],
    start: "",
    end: "",
    page: 1,
    page_size: 20,
    finish: false,
    loading: false,
  },
};

// 状态检查工具函数
const checkDenyPackStatus = (
  denyPack: Array<unknown>,
  denyPackType: number,
  general: number | unknown,
  channel: number | unknown,
): boolean => {
  if (!denyPack?.length) return true;

  const statusMap = {
    [DenyPackType.ExcludeAgent]: () => !denyPack.includes(general),
    [DenyPackType.IncludeAgent]: () => denyPack.includes(general),
    [DenyPackType.ExcludeChannel]: () => !denyPack.includes(channel),
    [DenyPackType.IncludeChannel]: () => denyPack.includes(channel),
  };

  return statusMap[denyPackType as keyof typeof statusMap]?.() ?? true;
};

// ========================
// 主 Hook（创建 store）
// ========================
export const useRepeatCompetition = () => {
  const [repeatCompetitionStore, setRepeatCompetitionStore] =
    createStore<RepeatCompetitionStore>({ ...initialCompetitionStore });

  const initData = async (): Promise<boolean> => {
    return new Promise((rs, rj) => {
      Promise.all([getRepeatCompetitionConfig(), getUserRankDetails()])
        .then((res) => {
          const list = repeatCompetitionStore.rank.filter((el) => {
            const find = repeatCompetitionStore.config[el.activity_id];
            if (!find) {
              return false;
            }

            if (!find?.status) return false;

            const denyPack = find.deny_pack || [];
            const denyPackType = find.deny_pack_type || 0;
            const userConfig = getUserChannel();
            // 检查 deny_pack 状态
            const denyPackStatus = checkDenyPackStatus(
              denyPack,
              denyPackType,
              userConfig.general,
              userConfig.channel,
            );
            return denyPackStatus;
          });
          setRepeatCompetitionStore("rank", reconcile(list));
          rs(true);
        })
        .catch(() => {
          rj(false);
        });
    });
  };

  const getRepeatCompetitionConfig = () => {
    GetRepeatConfigsByAtype({
      a_type: RepeatActivity.RepeatCompetition,
      is_simplify: false,
    })?.then((res) => {
      const cfgs = res?.cfgs || [];
      const val = cfgs.reduce(
        (acc, cur) => {
          acc[cur.activity_id as string] = cur;
          return acc;
        },
        {} as { [activity_id: string]: ActivityCfg },
      );

      setRepeatCompetitionStore("config", reconcile(val));
    });
  };

  const getUserRankDetails = async (): Promise<boolean> => {
    return new Promise((rs) => {
      GetUserRepeatRankDetails({
        has_list: true,
        page: 1,
        page_size: 5,
        has_prev: true,
        atype: RepeatActivity.RepeatCompetition,
      })?.then((details) => {
        setRepeatCompetitionStore("rank", reconcile(details.list || []));
        rs(true);
      });
    });
  };

  const getUserActivityDetails = async () => {
    return new Promise((rs, rj) => {
      GetRepeatDetailsByAtype({
        atype: RepeatActivity.RepeatCompetition,
      })
        ?.then((res) => {
          const list = res?.details || [];
          const val = list.reduce(
            (acc, cur) => {
              if (cur && cur.activity_id) {
                acc[cur.activity_id] = cur.details;
              }
              return acc;
            },
            {} as Record<string, RepeatActivityDetailItem>,
          );

          setRepeatCompetitionStore("details", reconcile(val));
        })
        .finally(() => {
          rs(true);
        });
    });
  };

  const initHistoryData = () => {
    setRepeatCompetitionStore(
      "history",
      reconcile(initialCompetitionStore.history),
    );
  };

  return {
    repeatCompetitionStore,
    setRepeatCompetitionStore,
    initData,
    getUserRankDetails,
    getUserActivityDetails,
    initHistoryData,
  };
};

// ========================
// Context & Provider hook
// ========================
export const CompetitionContext =
  createContext<ReturnType<typeof useRepeatCompetition>>();

export const useCompetitionContext = () => {
  const ctx = useContext(CompetitionContext);
  if (!ctx)
    throw new Error(
      "CompetitionContext must be used inside a <CompetitionContext.Provider>",
    );
  return ctx;
};
